import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useResetPassword } from '@/modules/auth/apiHooks';
import { useResetPasswordHook } from '@/modules/auth/components/FormsContainer/Password/logic';
import { IAuth } from '@/modules/auth/types';
import {
  Button,
  ErrorMessage,
  InputField,
  Typography,
} from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';

import Eye from '~/images/login/Eye.svg';
import EyeSlash from '~/images/login/EyeSlash.svg';

const PasswordForm: React.FC<{
  type: 'create' | 'reset';
}> = ({ type }) => {
  const { t } = useTranslation('login');
  const { push } = useRouter();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const { ResetPasswordFormValidationSchema } = useResetPasswordHook();
  const { mutate: resetPassword, isLoading: isResetPasswordLoading } =
    useResetPassword();
  const formMethods = useForm<IAuth.IResetPasswordSchema>({
    resolver: yupResolver(ResetPasswordFormValidationSchema),
    mode: 'onChange',
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = formMethods;

  const onSubmit = (data: IAuth.IResetPasswordSchema) => {
    resetPassword(data.password, {
      onSuccess: () => {
        toast.success(t('password-creation-successfully'));
        push(routes.login);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='flex flex-col gap-16'>
        <div className='flex flex-col gap-4'>
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-14'
            label={t('password')}
            placeholder={t('password')}
            error={!!errors.password}
            className='!rounded-xl bg-background-transparent'
            type={passwordVisible ? 'text' : 'password'}
            suffix={
              <div
                className='cursor-pointer'
                onClick={() => setPasswordVisible((prev) => !prev)}
              >
                {passwordVisible ? (
                  <Eye width='24' height='24' />
                ) : (
                  <EyeSlash width='24' height='24' />
                )}
              </div>
            }
            disabled={isResetPasswordLoading}
            {...register('password')}
          />
          {errors?.password ? (
            <ErrorMessage
              errorMessage={errors.password.message ?? ''}
              className='mt-0'
            />
          ) : (
            <Typography className='text-surface-10' variant='caption'>
              {t('password-requirements')}
            </Typography>
          )}
        </div>
        <InputField
          borderColor='border-outline-variant'
          width='w-full'
          py='!py-14'
          label={t('confirm-password')}
          placeholder={t('confirm-password')}
          error={!!errors.confirmPassword}
          className='!rounded-xl bg-background-transparent'
          type={confirmPasswordVisible ? 'text' : 'password'}
          suffix={
            <div
              className='cursor-pointer'
              onClick={() => setConfirmPasswordVisible((prev) => !prev)}
            >
              {confirmPasswordVisible ? (
                <Eye width='24' height='24' />
              ) : (
                <EyeSlash width='24' height='24' />
              )}
            </div>
          }
          disabled={isResetPasswordLoading}
          {...register('confirmPassword')}
        />
        <ErrorMessage
          errorMessage={errors.confirmPassword?.message ?? ''}
          className='mt-0'
        />
      </div>
      {/* Submit */}
      <Button
        size='full'
        type='submit'
        className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
        disabled={isResetPasswordLoading}
      >
        {type === 'reset' ? t('reset-password') : t('submit')}
      </Button>
    </form>
  );
};

export default PasswordForm;
